import { ArrowUpRight, CircleUserRound, Globe2, Mail, MessageCircle, Phone, Send } from 'lucide-react';
import { useEffect, useRef, useState, type FC, type FormEvent } from 'react';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, str } from '@/app/lib/a2ui/props.ts';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { cn } from '@/app/lib/utils';

type ContactRole = 'user' | 'agent';

interface ContactMessage {
  role: ContactRole;
  text: string;
}

function readContactMessages(value: unknown): ContactMessage[] {
  return arr(value).flatMap((entry) => {
    if (!isRecord(entry)) {
      return [];
    }
    const text = str(entry.text);
    const role = str(entry.role);
    if (!text || (role !== 'user' && role !== 'agent')) {
      return [];
    }
    return [{ role, text }];
  });
}

const CONTACT_DETAILS = {
  email: 'wayfarer@gamil.com',
  phone: '+9 871 34 56 78',
  phoneHref: 'tel:+9871345678',
  website: 'wayfarer.com',
  websiteHref: 'https://wayfarer.com',
  linkedin: 'linkedin.com/in/wayfarer/',
  linkedinHref: 'https://www.linkedin.com/in/wayfarer/',
} as const;

export const Contact: FC<A2uiNodeViewProps> = ({ node }) => {
  const dispatch = useSurfaceAction();
  const transcript = readContactMessages(node.props.messages);
  const [draft, setDraft] = useState('');
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const transcriptElement = transcriptRef.current;
    if (transcriptElement) {
      transcriptElement.scrollTop = transcriptElement.scrollHeight;
    }
  }, [transcript.length]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || !dispatch) {
      return;
    }
    dispatch('sendMessage', { message });
    setDraft('');
  };

  return (
    <div className="wayfarer-contact flex w-full flex-col gap-5" data-wayfarer-contact="true">
      <header className="max-w-2xl">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-caps text-primary">
          <MessageCircle className="h-4 w-4" aria-hidden="true" />
          <span>Contact Wayfarer</span>
        </p>
        <h1 className="mt-3 text-balance text-3xl font-bold leading-[1.1] tracking-tighter text-foreground sm:text-4xl">
          Let’s keep the conversation moving.
        </h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          Ask anything about your trip and see your question and our answer together.
        </p>
      </header>

      <div className="grid items-stretch gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(16rem,0.7fr)]">
        <section className="flex h-[32rem] min-h-0 min-w-0 flex-col overflow-hidden rounded-[15px] border border-border bg-card p-5 shadow-lift sm:h-[36rem]">
          <div className="flex shrink-0 items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
              aria-hidden="true"
            >
              <MessageCircle className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="text-lg font-bold tracking-tight text-foreground">Your conversation</h2>
            </div>
          </div>

          <div
            ref={transcriptRef}
            className="mt-5 min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-[15px] border border-border bg-background/45 p-5"
            aria-live="polite"
            aria-label="Your conversation"
            role="log"
          >
            <div className="flex min-h-full flex-col justify-end gap-3">
              {transcript.length > 0 ? (
                transcript.map((message, index) => {
                  const isVisitor = message.role === 'user';
                  return (
                    <div
                      key={`${message.role}-${index}`}
                      className={cn('flex w-full', isVisitor ? 'justify-end' : 'justify-start')}
                    >
                      <div
                        className={cn(
                          'max-w-[88%] rounded-[15px] px-4 py-3 text-sm leading-relaxed shadow-sm',
                          isVisitor
                            ? 'bg-primary text-primary-foreground'
                            : 'border border-border bg-muted text-foreground',
                        )}
                      >
                        <p
                          className={cn(
                            'mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-caps',
                            isVisitor ? 'text-primary-foreground/75' : 'text-muted-foreground',
                          )}
                        >
                          <CircleUserRound className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span>{isVisitor ? 'You' : 'Wayfarer'}</span>
                        </p>
                        <p className="whitespace-pre-wrap break-words">{message.text}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="grid min-h-full place-items-center p-6 text-center">
                  <div>
                    <MessageCircle className="mx-auto h-7 w-7 text-muted-foreground-subtle" aria-hidden="true" />
                    <p className="mt-3 text-sm text-muted-foreground">
                      Your messages will appear here as we talk.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <form
            className="mt-4 flex shrink-0 items-center gap-2 rounded-full border border-border bg-background/45 p-1.5 shadow-sm"
            onSubmit={handleSubmit}
          >
            <label className="sr-only" htmlFor="wayfarer-contact-message">
              Where to next?
            </label>
            <input
              id="wayfarer-contact-message"
              className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:outline-none"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Where to next?"
              autoComplete="off"
            />
            <button
              type="submit"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Send message"
              disabled={!draft.trim() || !dispatch}
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </section>

        <aside className="relative overflow-hidden rounded-[15px] border border-border bg-card p-5 shadow-lift">
          <div
            className="absolute inset-x-5 top-0 h-px rounded-full bg-gradient-to-r from-primary/0 via-primary to-primary/0"
            aria-hidden="true"
          />
          <div className="flex items-start gap-3 pt-1">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
              aria-hidden="true"
            >
              <Globe2 className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-foreground">Contact details</h2>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Prefer a direct line? Reach us here.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <a
              className="group flex items-center gap-3 rounded-[15px] border border-border bg-background/45 p-3 transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
              href={`mailto:${CONTACT_DETAILS.email}`}
            >
              <Mail className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-[10px] font-bold uppercase tracking-caps text-muted-foreground">Email</span>
                <span className="mt-1 block break-all text-sm font-medium text-foreground" translate="no">
                  {CONTACT_DETAILS.email}
                </span>
              </span>
            </a>

            <a
              className="group flex items-center gap-3 rounded-[15px] border border-border bg-background/45 p-3 transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
              href={CONTACT_DETAILS.phoneHref}
            >
              <Phone className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-[10px] font-bold uppercase tracking-caps text-muted-foreground">Phone</span>
                <span className="mt-1 block text-sm font-medium text-foreground" translate="no">
                  {CONTACT_DETAILS.phone}
                </span>
              </span>
            </a>

            <a
              className="group flex items-center gap-3 rounded-[15px] border border-border bg-background/45 p-3 transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
              href={CONTACT_DETAILS.websiteHref}
              target="_blank"
              rel="noreferrer"
            >
              <Globe2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-caps text-muted-foreground">Website</span>
                <span className="mt-1 block text-sm font-medium text-foreground" translate="no">
                  {CONTACT_DETAILS.website}
                </span>
              </span>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden="true" />
            </a>

            <a
              className="group flex items-center gap-3 rounded-[15px] border border-border bg-background/45 p-3 transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
              href={CONTACT_DETAILS.linkedinHref}
              target="_blank"
              rel="noreferrer"
            >
              <Globe2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-bold uppercase tracking-caps text-muted-foreground">LinkedIn</span>
                <span className="mt-1 block text-sm font-medium text-foreground" translate="no">
                  {CONTACT_DETAILS.linkedin}
                </span>
              </span>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden="true" />
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
};
